import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { gmbAPI } from '../lib/api';

const GMBContext = createContext(null);

// Mock GMB Profile Data - used ONLY when user connects via mock flow (no OAuth)
const MOCK_GMB_PROFILE = {
  id: 'gmb_123456789',
  name: 'Sunrise Coffee House',
  category: 'Coffee Shop',
  address: '123 Main Street, Downtown, Mumbai 400001',
  city: 'Mumbai',
  phone: '+91 98765 43210',
  website: 'https://sunrisecoffee.in',
  description: 'Artisan coffee shop serving premium single-origin coffee and fresh pastries since 2018.',
  rating: 4.6,
  reviewCount: 287,
  photos: 45,
  isVerified: true,
  profileImage: 'https://images.unsplash.com/photo-1501339847302-ac426a4a7cbb?w=200&h=200&fit=crop',
  coverImage: 'https://images.unsplash.com/photo-1554118811-1e0d58224f24?w=800&h=400&fit=crop',
  openingHours: {
    monday: '7:00 AM - 10:00 PM', tuesday: '7:00 AM - 10:00 PM',
    wednesday: '7:00 AM - 10:00 PM', thursday: '7:00 AM - 10:00 PM',
    friday: '7:00 AM - 11:00 PM', saturday: '8:00 AM - 11:00 PM',
    sunday: '8:00 AM - 9:00 PM'
  },
  attributes: ['Dine-in', 'Takeaway', 'Delivery', 'Free WiFi', 'Outdoor seating'],
  auditScore: 78,
  auditIssues: [
    { severity: 'high', title: 'Missing business hours for holidays', description: 'Add special hours for upcoming holidays.' },
    { severity: 'medium', title: 'Description could be more keyword-rich', description: 'Include more local keywords.' },
    { severity: 'low', title: 'Add more photos', description: 'Businesses with 100+ photos get 520% more calls.' }
  ],
  suggestions: [
    'Post about your weekend specials every Friday',
    'Reply to all reviews within 24 hours',
    'Add photos of your best-selling items'
  ],
  recentPosts: [
    { id: 'post_1', content: 'Start your Monday right with our new Caramel Macchiato!', image: 'https://images.unsplash.com/photo-1485808191679-5f86510681a2?w=600&h=400&fit=crop', date: '2024-01-15', views: 1250, clicks: 89, type: 'offer' },
    { id: 'post_2', content: 'Thank you Mumbai for voting us Best Coffee Shop 2024!', image: 'https://images.unsplash.com/photo-1495474472287-4d71bcdd2085?w=600&h=400&fit=crop', date: '2024-01-10', views: 2340, clicks: 156, type: 'update' }
  ],
  topPerformingContent: [
    { type: 'Offers & Discounts', avgViews: 1850, suggestion: 'Post offers on weekends' },
    { type: 'New Products', avgViews: 1200, suggestion: 'Include high-quality photos' }
  ]
};

export const useGMB = () => {
  const context = useContext(GMBContext);
  if (!context) throw new Error('useGMB must be used within a GMBProvider');
  return context;
};

export const GMBProvider = ({ children }) => {
  const [gmbProfile, setGMBProfile] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  const [connectionMethod, setConnectionMethod] = useState(null); // 'oauth' or 'mock'
  const [googleEmail, setGoogleEmail] = useState('');
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [locationError, setLocationError] = useState('');
  const [authToken, setAuthToken] = useState(() => localStorage.getItem('gmb_token'));

  // Watch for token changes (login/logout)
  useEffect(() => {
    const interval = setInterval(() => {
      const current = localStorage.getItem('gmb_token');
      if (current !== authToken) {
        setAuthToken(current);
      }
    }, 500);
    return () => clearInterval(interval);
  }, [authToken]);

  const checkStatus = useCallback(async () => {
    const token = localStorage.getItem('gmb_token');
    if (!token) {
      setGMBProfile(null);
      setIsConnected(false);
      setConnectionMethod(null);
      setGoogleEmail('');
      setLocations([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const response = await gmbAPI.status();
      const data = response.data;

      if (data.connected) {
        // Real OAuth connection
        setIsConnected(true);
        setConnectionMethod('oauth');
        setGoogleEmail(data.google_email || '');
        setLocations(data.locations || []);
        // Set a profile based on locations if available, otherwise show google-connected state
        if (data.locations && data.locations.length > 0) {
          const loc = data.locations[0];
          setGMBProfile({
            ...MOCK_GMB_PROFILE,
            name: loc.name || 'Your Business',
            category: loc.category || 'Business',
            address: (loc.address || []).join(', ') || '',
            phone: loc.phone || '',
            website: loc.website || '',
            googleEmail: data.google_email,
            isOAuth: true
          });
        } else {
          // OAuth connected but no locations fetched yet (GBP API may not be approved)
          setGMBProfile({
            name: data.google_email || 'Your Google Business',
            category: 'Google Business Profile',
            address: '',
            city: '',
            phone: '',
            website: '',
            description: '',
            rating: 0,
            reviewCount: 0,
            photos: 0,
            isVerified: false,
            profileImage: `https://ui-avatars.com/api/?name=${encodeURIComponent(data.google_email || 'GB')}&background=4285F4&color=fff&size=200`,
            coverImage: '',
            openingHours: {},
            attributes: [],
            auditScore: 0,
            auditIssues: [],
            suggestions: [],
            recentPosts: [],
            topPerformingContent: [],
            googleEmail: data.google_email,
            isOAuth: true
          });
        }
      } else {
        // Not OAuth connected - check legacy mock
        const mockConnected = localStorage.getItem('gmb_connected');
        if (mockConnected === 'true') {
          setGMBProfile(MOCK_GMB_PROFILE);
          setIsConnected(true);
          setConnectionMethod('mock');
        }
      }
    } catch {
      const mockConnected = localStorage.getItem('gmb_connected');
      if (mockConnected === 'true') {
        setGMBProfile(MOCK_GMB_PROFILE);
        setIsConnected(true);
        setConnectionMethod('mock');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkStatus();
  }, [checkStatus, authToken]);

  const connectGMB = async () => {
    setLoading(true);
    try {
      const response = await gmbAPI.connect();
      if (response.data.auth_url) {
        window.location.href = response.data.auth_url;
        return;
      }
    } catch { /* fallback to mock */ }
    localStorage.setItem('gmb_connected', 'true');
    setGMBProfile(MOCK_GMB_PROFILE);
    setIsConnected(true);
    setConnectionMethod('mock');
    setLoading(false);
  };

  const disconnectGMB = async () => {
    try {
      if (connectionMethod === 'oauth') {
        await gmbAPI.disconnect();
      }
    } catch { /* ignore */ }
    localStorage.removeItem('gmb_connected');
    setGMBProfile(null);
    setIsConnected(false);
    setConnectionMethod(null);
    setGoogleEmail('');
    setLocations([]);
    setLocationError('');
  };

  const fetchLocations = async () => {
    setLocationError('');
    try {
      const response = await gmbAPI.locations();
      const locs = response.data.locations || [];
      setLocations(locs);
      if (locs.length > 0) {
        const loc = locs[0];
        setGMBProfile(prev => ({
          ...prev,
          name: loc.name || prev?.name,
          category: loc.category || prev?.category,
          address: (loc.address || []).join(', ') || prev?.address,
        }));
      }
      return locs;
    } catch (err) {
      const detail = err.response?.data?.detail || 'Failed to fetch locations';
      setLocationError(detail);
      return [];
    }
  };

  const publishToGMB = async (locationId, postText, imageUrl) => {
    const response = await gmbAPI.publish({
      location_id: locationId,
      post_text: postText,
      image_url: imageUrl,
      post_type: 'STANDARD'
    });
    return response.data;
  };

  return (
    <GMBContext.Provider value={{
      gmbProfile,
      isConnected,
      connectionMethod,
      googleEmail,
      locations,
      locationError,
      loading,
      connectGMB,
      disconnectGMB,
      fetchLocations,
      publishToGMB,
      refreshProfile: checkStatus
    }}>
      {children}
    </GMBContext.Provider>
  );
};

export default GMBContext;
